import { API_BASE } from '../../constants'
import { normalizeCategory, normalizeProduct } from '../../normalizers'
import { normalizeCatalogProductFieldDefinition } from '../../pluginFields'
import type {
  CatalogCategory,
  CatalogPagination,
  CatalogProduct,
  CatalogProductFieldDefinition,
} from '../../types'
import { fetchWithPolicy } from '../../../shared/api/fetchPolicy'
import { requestCatalogJson, toRecordArray } from './catalogApi.shared'
import type { NumericFilterState, OptionFilterState, SearchFacets, SearchSort } from '../../components/search-results/types'

export type CatalogProductListingFilters = {
  selectedBrands?: string[]
  selectedTypes?: string[]
  priceRange?: { min: string; max: string }
  numericFilters?: NumericFilterState
  optionFilters?: OptionFilterState
  sort?: SearchSort
}

export const fetchCatalogCategories = async (signal: AbortSignal): Promise<CatalogCategory[]> => {
  const query = new URLSearchParams({
    page: '1',
    limit: '100',
    sortBy: 'order',
    sortOrder: 'asc',
  })
  const data = await requestCatalogJson<{ categories?: unknown }>({
    signal,
    path: `/catalog/categories?${query.toString()}`,
    fallbackError: 'Errore caricamento categorie',
  })
  return toRecordArray(data.categories)
    .map((item) => normalizeCategory(item))
    .filter((category) => category._id && category.slug)
}

export const fetchCategoryProducts = async (
  categoryId: string,
  signal: AbortSignal,
  options?: {
    page?: number
    limit?: number
    filters?: CatalogProductListingFilters
    primaryCategoryId?: string
  },
): Promise<{ products: CatalogProduct[]; pagination: CatalogPagination; facets?: SearchFacets }> => {
  const query = new URLSearchParams({ categoryId, status: 'active' })
  if (options?.primaryCategoryId?.trim()) query.set('primaryCategoryId', options.primaryCategoryId.trim())
  if (options?.page && Number.isFinite(options.page) && options.page > 0) {
    query.set('page', String(options.page))
  }
  if (options?.limit && Number.isFinite(options.limit) && options.limit > 0) {
    query.set('limit', String(options.limit))
  }
  const filters = options?.filters
  filters?.selectedBrands?.forEach((value) => query.append('brand', value))
  filters?.selectedTypes?.forEach((value) => query.append('type', value))
  if (filters?.priceRange?.min?.trim()) query.set('price_min', filters.priceRange.min.trim())
  if (filters?.priceRange?.max?.trim()) query.set('price_max', filters.priceRange.max.trim())
  Object.entries(filters?.numericFilters ?? {}).forEach(([id, range]) => {
    if (range.min.trim()) query.set(`nmin:${id}`, range.min.trim())
    if (range.max.trim()) query.set(`nmax:${id}`, range.max.trim())
  })
  Object.entries(filters?.optionFilters ?? {}).forEach(([id, values]) => {
    values.forEach((value) => query.append(`of:${id}`, value))
  })
  if (filters?.sort) {
    const [sortBy, sortOrder] = filters.sort.split('-')
    query.set('sortBy', sortBy === 'updated' ? 'updatedAt' : sortBy)
    query.set('sortOrder', sortOrder)
  }
  const data = await requestCatalogJson<{ products?: unknown }>({
    signal,
    path: `/catalog/products?${query.toString()}`,
    fallbackError: 'Errore caricamento prodotti categoria',
  })
  const products = toRecordArray(data.products)
    .map((item) => normalizeProduct(item))
    .filter((product) => product._id && product.slug)
  const rawPagination =
    data && typeof data === 'object' && 'pagination' in data
      ? (data as { pagination?: unknown }).pagination
      : undefined
  const pagination =
    rawPagination && typeof rawPagination === 'object'
      ? {
          page: Number((rawPagination as { page?: unknown }).page ?? 1) || 1,
          limit: Number((rawPagination as { limit?: unknown }).limit ?? products.length) || products.length,
          total: Number((rawPagination as { total?: unknown }).total ?? products.length) || products.length,
          totalPages: Number((rawPagination as { totalPages?: unknown }).totalPages ?? 1) || 1,
          hasNextPage: Boolean((rawPagination as { hasNextPage?: unknown }).hasNextPage),
          hasPreviousPage: Boolean((rawPagination as { hasPreviousPage?: unknown }).hasPreviousPage),
        }
      : {
          page: 1,
          limit: products.length,
          total: products.length,
          totalPages: 1,
          hasNextPage: false,
          hasPreviousPage: false,
        }
  const facets = data && typeof data === 'object' && 'facets' in data
    ? (data as { facets?: SearchFacets }).facets
    : undefined
  return { products, pagination, facets }
}

export const fetchProductFieldDefinitions = async (
  signal: AbortSignal,
): Promise<CatalogProductFieldDefinition[]> => {
  let response: Response
  try {
    response = await fetchWithPolicy({
      request: `${API_BASE}/catalog/product-field-definitions`,
      init: { signal },
      retries: 0,
    })
  } catch {
    return []
  }
  if (!response.ok) return []

  const payload = (await response.json().catch(() => ({}))) as { fields?: unknown }

  const fields = toRecordArray(payload.fields)
    .map((entry) => normalizeCatalogProductFieldDefinition(entry))
    .filter((entry): entry is CatalogProductFieldDefinition => Boolean(entry))

  fields.sort((left, right) => {
    if (left.storefront.zone !== right.storefront.zone) {
      return left.storefront.zone.localeCompare(right.storefront.zone)
    }
    if (left.storefront.order !== right.storefront.order) {
      return left.storefront.order - right.storefront.order
    }
    return left.label.localeCompare(right.label, 'it', { sensitivity: 'base' })
  })

  return fields
}
